import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useTranslate } from 'react-admin';

interface HeatmapData {
  hour: number;
  day: number;
  count: number;
}

interface UsageHeatmapProps {
  data: HeatmapData[];
  loading?: boolean;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => `${i}:00`);

/**
 * Hourly Usage Heatmap Component
 *
 * Displays a 24x7 heatmap showing session counts by hour of day and day of week
 */
export const UsageHeatmap: React.FC<UsageHeatmapProps> = ({ data, loading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const translate = useTranslate();

  // Find max count for color scaling
  const maxCount = Math.max(...data.map(d => d.count), 1);

  // Get color intensity based on count (0-1 scale)
  const getColor = (count: number) => {
    if (count === 0) return '#F5F5F5';

    const intensity = count / maxCount;

    // Use primary color with varying opacity
    if (intensity < 0.2) return '#E8F5E9'; // Very light green
    if (intensity < 0.4) return '#C8E6C9'; // Light green
    if (intensity < 0.6) return '#81C784'; // Medium green
    if (intensity < 0.8) return '#4CAF50'; // Green
    return '#2E7D32'; // Dark green
  };

  // Get cell data for specific hour and day
  const getCellData = (hour: number, day: number) => {
    return data.find(d => d.hour === hour && d.day === day);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Typography variant="body2" color="text.secondary">
          Loading heatmap...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      {/* Day labels */}
      <Box sx={{ display: 'flex', mb: 1, ml: { xs: '35px', sm: '50px' } }}>
        {DAY_LABELS.map((day, idx) => (
          <Box
            key={day}
            sx={{
              width: { xs: 34, sm: 50 },
              textAlign: 'center',
              fontSize: { xs: '10px', sm: '12px' },
              fontWeight: 600,
              color: '#606060',
            }}
          >
            {isMobile ? day.charAt(0) : day}
          </Box>
        ))}
      </Box>

      {/* Heatmap grid */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {HOUR_LABELS.map((hourLabel, hourIdx) => (
          <Box key={hourLabel} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {/* Hour label */}
            <Box
              sx={{
                width: { xs: '30px', sm: '45px' },
                textAlign: 'right',
                pr: { xs: 0.5, sm: 1 },
                fontSize: { xs: '9px', sm: '11px' },
                color: '#606060',
              }}
            >
              {hourLabel}
            </Box>

            {/* Day cells */}
            {DAY_LABELS.map((_, dayIdx) => {
              const cellData = getCellData(hourIdx, dayIdx);
              const count = cellData?.count || 0;

              return (
                <Box
                  key={`${hourIdx}-${dayIdx}`}
                  sx={{
                    width: { xs: 32, sm: 48 },
                    height: { xs: 16, sm: 20 },
                    backgroundColor: getColor(count),
                    border: '1px solid #FFFFFF',
                    borderRadius: 0.5,
                    ml: 0.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: '8px', sm: '10px' },
                    fontWeight: count > 0 ? 600 : 400,
                    color: count > maxCount * 0.6 ? '#FFFFFF' : '#1A1A1A',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      zIndex: 10,
                    },
                  }}
                  title={`${DAY_LABELS[dayIdx]} ${hourLabel}: ${count} sessions`}
                >
                  {count > 0 && count}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 3, ml: { xs: '35px', sm: '50px' } }}>
        <Typography variant="caption" sx={{ mr: 2, color: '#606060' }}>
          Sessions:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 30, height: 15, backgroundColor: '#F5F5F5', border: '1px solid #E0E0E0' }} />
          <Typography variant="caption" sx={{ color: '#606060', mr: 2 }}>0</Typography>

          <Box sx={{ width: 30, height: 15, backgroundColor: '#E8F5E9', border: '1px solid #E0E0E0' }} />
          <Typography variant="caption" sx={{ color: '#606060', mr: 2 }}>Low</Typography>

          <Box sx={{ width: 30, height: 15, backgroundColor: '#81C784', border: '1px solid #E0E0E0' }} />
          <Typography variant="caption" sx={{ color: '#606060', mr: 2 }}>Medium</Typography>

          <Box sx={{ width: 30, height: 15, backgroundColor: '#2E7D32', border: '1px solid #E0E0E0' }} />
          <Typography variant="caption" sx={{ color: '#606060' }}>High</Typography>
        </Box>
      </Box>
    </Box>
  );
};
